import { useEffect, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

interface FilterDropdownProps {
  trigger: ReactNode
  children: ReactNode
  align?: "left" | "right"
}

export function FilterDropdown({
  trigger,
  children,
  align = "left",
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false)
    }
    window.addEventListener("scroll", handleScroll, true)
    window.addEventListener("resize", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("resize", handleScroll)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  const toggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const scrollY = window.scrollY
      const scrollX = window.scrollX
      let top = rect.bottom + scrollY + 4
      let left = rect.left + scrollX

      if (align === "right") {
        left = rect.right + scrollX - 160
      }

      setPosition({ top, left })
    }
    setIsOpen(!isOpen)
  }

  const handleMenuClick = () => {
    setIsOpen(false)
  }

  return (
    <>
      <div ref={triggerRef} onClick={toggleOpen} className="inline-block cursor-pointer">
        {trigger}
      </div>
      {isOpen
        ? createPortal(
            <div
              ref={menuRef}
              onClick={handleMenuClick}
              style={{ top: position.top, left: position.left }}
              className="absolute z-50 min-w-[240px] max-h-[70vh] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md-0"
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
